import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MailIcon } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';

import { ColumnDef } from 'features/common/hooks/useColumnSelection';
import { EntriesData, LotteryEntryStatus, QueryResultProps } from 'features/LotteryEntries/types';
import { getStatus } from 'features/LotteryEntries/components/RunLotteryModal/utils';
import {
	PunchupTableContainer,
	PunchupHeaderRow,
	PunchupColumnHeader,
	PunchupDataRow,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { PunchupLoadMore } from 'features/common/components/Punchup/composites/tables/PunchupLoadMore';
import { PunchupTag } from 'features/common/components/Punchup/primitives/display/PunchupTag';
import { post } from 'services/api';
import { postToast } from 'features/common/utils/postFlash';
import { DEFAULT_STATUS_BACKGROUND, STATUS_BACKGROUND } from './constants';
import styles from './PunchupLotteryTable.module.css';

interface PunchupLotteryTableProps {
	entriesQueryObject: QueryResultProps;
	visibleDefs: ColumnDef[];
	gridColumns: string;
	/** True while an already-loaded table re-fetches — dims the table with a spinner overlay. */
	isRefetching?: boolean;
}

const RESENDABLE_STATUSES: readonly LotteryEntryStatus[] = ['winner', 'claimed'];

const usePunchupResendEmail = () => {
	const { t } = useTranslation();
	const { mutate: resendEmail } = useMutation({
		mutationFn: (entryId: number) => post(`/lottery/entries/${entryId}/email`),
		onSuccess: () => postToast('success', t('lottery-email-sent', 'Email sent successfully')),
		onError: () => postToast('error', t('lottery-email-failed', 'Failed to send email')),
	});
	return resendEmail;
};

const renderCell = (defKey: string, entry: EntriesData, t: (key: string, fallback?: string) => string): ReactNode => {
	switch (defKey) {
		case 'email':
			return entry.Email;
		case 'firstName':
			return entry.FirstName;
		case 'lastName':
			return entry.LastName;
		case 'status':
			return (
				<PunchupTag
					label={getStatus(entry.Status)}
					background={STATUS_BACKGROUND[entry.Status] ?? DEFAULT_STATUS_BACKGROUND}
					size="sm"
				/>
			);
		case 'numberOfTickets':
			return entry.NumberOfTickets;
		case 'wheelchairAccess':
			return entry.WheelchairAccess ? t('yes', 'Yes') : t('no', 'No');
		case 'zipCode':
			return entry.ZipCode;
		case 'optOutMailing':
			return entry.OptOutMailing ? t('yes', 'Yes') : t('no', 'No');
		case 'gender':
			return entry.Gender;
		case 'age':
			return entry.Age;
		case 'ethnicity':
			return entry.Ethnicity;
		case 'householdIncome':
			return entry.HouseholdIncome;
		case 'identifyGroups':
			return entry.IdentifyGroups;
		default:
			return null;
	}
};

export const PunchupLotteryTable: FC<PunchupLotteryTableProps> = ({
	entriesQueryObject,
	visibleDefs,
	gridColumns,
	isRefetching,
}) => {
	const { t, i18n } = useTranslation();
	const resendEmail = usePunchupResendEmail();
	const { data: entriesPages, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = entriesQueryObject;

	const allEntries: EntriesData[] = entriesPages?.pages?.flat() ?? [];

	return (
		<>
			<PunchupTableContainer columns={gridColumns} isRefetching={isRefetching}>
				<thead>
					<PunchupHeaderRow>
						{visibleDefs.map((def) => (
							<PunchupColumnHeader key={def.key}>
								{i18n.exists(def.labelKey) ? t(def.labelKey) : def.labelKey}
							</PunchupColumnHeader>
						))}
						<PunchupColumnHeader key="action" />
					</PunchupHeaderRow>
				</thead>
				<tbody>
					{isLoading && (
						<tr>
							<td colSpan={visibleDefs.length + 1} className={styles.emptyCell}>
								{t('loading', 'Loading...')}
							</td>
						</tr>
					)}
					{!isLoading && allEntries.length === 0 && (
						<tr>
							<td colSpan={visibleDefs.length + 1} className={styles.emptyCell}>
								{t('lottery-no-entries', 'No lottery entries found.')}
							</td>
						</tr>
					)}
					{allEntries.map((entry) => (
						<PunchupDataRow key={entry.ID}>
							{visibleDefs.map((def) => (
								<td key={def.key}>{renderCell(def.key, entry, t)}</td>
							))}
							<td>
								{RESENDABLE_STATUSES.includes(entry.Status) && (
									<button
										className={styles.resendButton}
										onClick={() => resendEmail(entry.ID)}
										title={t('lottery-resend-email', 'Resend email')}
										type="button"
									>
										<MailIcon size={16} />
									</button>
								)}
							</td>
						</PunchupDataRow>
					))}
				</tbody>
			</PunchupTableContainer>
			<PunchupLoadMore isDisabled={!hasNextPage} isLoading={isFetchingNextPage} handleClick={() => fetchNextPage()} />
		</>
	);
};
